import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Bell, Plus, LogOut, TrendingUp, Calendar, AlertCircle, Upload, QrCode, MessageCircle, Loader2, X, Send, User, ChevronUp, ChevronDown, ArrowUpDown, Circle, CheckCircle2, Phone, Clock, HelpCircle, AlertTriangle, RefreshCw, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UpgradeModal from '../components/UpgradeModal';
import Onboarding from '../components/Onboarding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para formatar data no padrão brasileiro (DD/MM/AAAA)
const formatarDataBR = (dataString) => {
  if (!dataString) return '-';
  try {
    // Se já estiver no formato DD/MM/AAAA, retorna como está
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataString)) {
      return dataString;
    }
    // Converte de YYYY-MM-DD para DD/MM/AAAA
    const [ano, mes, dia] = dataString.split('-');
    return `${dia}/${mes}/${ano}`;
  } catch (e) {
    return dataString;
  }
};

// ── Modal de Envio WhatsApp ─────────────────────────────────────────────────
const WhatsAppModal = ({ lembrete, user, onClose, onSend, enviando }) => {
  // Gerar mensagem padrão
  const gerarMensagemPadrao = () => {
    const clienteNome = lembrete.cliente_nome || 'Cliente';
    const tipoServico = lembrete.tipo_servico || 'Serviço';
    const dataRenovacao = lembrete.data_renovacao || '';
    const nomeEmpresa = user?.nome_empresa || 'Empresa';
    const telefoneEmpresa = user?.telefone || '';

    // Calcular dias restantes
    let diasRestantes = 0;
    try {
      const dtRenovacao = new Date(dataRenovacao);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      dtRenovacao.setHours(0, 0, 0, 0);
      diasRestantes = Math.ceil((dtRenovacao - hoje) / (1000 * 60 * 60 * 24));
    } catch (e) {
      diasRestantes = 0;
    }

    // Formatar data
    let dataFormatada = dataRenovacao;
    try {
      const dt = new Date(dataRenovacao);
      dataFormatada = dt.toLocaleDateString('pt-BR');
    } catch (e) {}

    // Template baseado nos dias
    let urgencia, corpo;
    if (diasRestantes < 0) {
      urgencia = "⚠️ *VENCIDO*";
      corpo = `Seu serviço *${tipoServico}* venceu há *${Math.abs(diasRestantes)} dia${Math.abs(diasRestantes) > 1 ? 's' : ''}* (${dataFormatada}).`;
    } else if (diasRestantes === 0) {
      urgencia = "⚠️ *VENCE HOJE*";
      corpo = `Seu serviço *${tipoServico}* vence *hoje* (${dataFormatada}).`;
    } else if (diasRestantes <= 7) {
      urgencia = `🔔 *Lembrete Urgente — ${diasRestantes} dia${diasRestantes > 1 ? 's' : ''}*`;
      corpo = `Seu serviço *${tipoServico}* vence em *${diasRestantes} dia${diasRestantes > 1 ? 's' : ''}* (${dataFormatada}).`;
    } else if (diasRestantes <= 30) {
      urgencia = `📅 *Lembrete — ${diasRestantes} dias*`;
      corpo = `Seu serviço *${tipoServico}* vence em *${diasRestantes} dias* (${dataFormatada}).`;
    } else {
      urgencia = `📋 *Informativo*`;
      corpo = `Seu serviço *${tipoServico}* tem renovação prevista para *${dataFormatada}*.`;
    }

    // Montar link do WhatsApp do profissional
    let linkProfissional = '';
    if (telefoneEmpresa) {
      const waNum = '55' + telefoneEmpresa.replace(/\D/g, '').replace(/^55/, '');
      const msgPre = encodeURIComponent(`Olá ${nomeEmpresa}, recebi seu lembrete sobre ${tipoServico}. Gostaria de mais informações.`);
      linkProfissional = `\n💬 Fale com *${nomeEmpresa}*:\nhttps://wa.me/${waNum}?text=${msgPre}`;
    }

    return (
      `${urgencia}\n\n` +
      `Olá, *${clienteNome}*! 👋\n\n` +
      `${corpo}\n\n` +
      `Entre em contato conosco para renovar ou tirar dúvidas.${linkProfissional}\n\n` +
      `_Enviado por ${nomeEmpresa} via Lembrou_`
    );
  };

  const [mensagem, setMensagem] = useState(gerarMensagemPadrao());

  const [enviandoLocal, setEnviandoLocal] = useState(false);

  const handleEnviar = () => {
    if (enviandoLocal || enviando) return; // Guard contra duplo clique
    setEnviandoLocal(true);
    onSend(lembrete, mensagem);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-green-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Enviar WhatsApp</h2>
              <p className="text-sm text-gray-500">Para: {lembrete.cliente_nome}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Info do destinatário */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Telefone:</span>
              <span className="font-medium text-gray-900">{lembrete.cliente_telefone}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Serviço:</span>
              <span className="font-medium text-gray-900">{lembrete.tipo_servico}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Renovação:</span>
              <span className="font-medium text-gray-900">{formatarDataBR(lembrete.data_renovacao)}</span>
            </div>
          </div>

          {/* Mensagem editável */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem (você pode editar antes de enviar)
            </label>
            <Textarea
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={10}
              className="w-full rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500 text-sm font-mono"
              placeholder="Digite a mensagem..."
            />
            <p className="text-xs text-gray-400 mt-1">
              Use *texto* para negrito e _texto_ para itálico no WhatsApp
            </p>
          </div>

          {/* Botão restaurar */}
          <button
            type="button"
            onClick={() => setMensagem(gerarMensagemPadrao())}
            className="text-sm text-green-600 hover:text-green-700 font-medium"
          >
            ↺ Restaurar mensagem padrão
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl"
            disabled={enviando}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviar}
            disabled={enviando || enviandoLocal || !mensagem.trim()}
            className="rounded-xl bg-green-600 hover:bg-green-700 text-white"
          >
            {enviando ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar WhatsApp
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Dashboard Principal ─────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [lembretes, setLembretes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  
  // Modal WhatsApp
  const [confirmModal, setConfirmModal] = useState(null); // { id, nome }
  const [whatsappModal, setWhatsappModal] = useState(null); // lembrete selecionado
  const [enviandoWhatsApp, setEnviandoWhatsApp] = useState(false);
  const [whatsappResultModal, setWhatsappResultModal] = useState(null); // { tipo: 'sucesso'|'erro'|'bloqueado', ... }
  const [whatsappEnviadoHoje, setWhatsappEnviadoHoje] = useState({}); // { [lembreteId]: "HH:MM" }


  // Pendentes (não-corretores)
  const [showPendentes, setShowPendentes] = useState(false);
  const [processandoComparecimento, setProcessandoComparecimento] = useState(null);
  const [enviandoRemarcacao, setEnviandoRemarcacao] = useState(null);
  const [renovandoLembrete, setRenovandoLembrete] = useState(null);

  // Paginação (para todos)
  const [currentPage, setCurrentPage] = useState(1);
  const [mesFiltroAtivo, setMesFiltroAtivo] = useState(null); // NOVO: filtro por mês
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Ordenação (para todos)
  const [sortField, setSortField] = useState('data_renovacao'); // 'data_renovacao' ou 'status'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' ou 'desc'

  // Verifica se é corretor de seguros
  const isCorretorSeguros = user?.tipo_profissao?.toLowerCase() === 'corretor de seguros';

  // Função para verificar se o lembrete está vencido
  const isVencido = (dataRenovacao) => {
    if (!dataRenovacao) return false;
    try {
      const dtRenovacao = new Date(dataRenovacao);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      dtRenovacao.setHours(0, 0, 0, 0);
      return dtRenovacao < hoje;
    } catch {
      return false;
    }
  };

  // Status disponíveis para corretores (ordem de prioridade para ordenação)
  const STATUS_CORRETOR = {
    'nao_renovado': { label: 'Não Renovado', color: 'bg-red-500', order: 1 },
    'contatado': { label: 'Contatado', color: 'bg-yellow-500', order: 2 },
    'em_andamento': { label: 'Em Andamento', color: 'bg-blue-500', order: 3 },
    'renovado': { label: 'Renovado', color: 'bg-green-500', order: 4 }
  };

  useEffect(() => {
    const userData = localStorage.getItem('lembrou_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('lembrou_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, lembretesRes, whatsappRes, enviadosHojeRes, perfilRes] = await Promise.all([
        axios.get(`${API}/lembretes/stats`, config),
        axios.get(`${API}/lembretes`, config),
        axios.get(`${API}/whatsapp/status`, config).catch(() => ({ data: { habilitado: false } })),
        axios.get(`${API}/whatsapp/enviados-hoje`, config).catch(() => ({ data: { enviados: {} } })),
        axios.get(`${API}/perfil`, config).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setLembretes(lembretesRes.data);
      setWhatsappStatus(whatsappRes.data);
      setWhatsappEnviadoHoje(enviadosHojeRes.data?.enviados || {});

      // Atualizar user com dados frescos do backend (inclui telefone)
      if (perfilRes.data) {
        const userAtualizado = { ...JSON.parse(localStorage.getItem('lembrou_user') || '{}'), ...perfilRes.data };
        localStorage.setItem('lembrou_user', JSON.stringify(userAtualizado));
        setUser(userAtualizado);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lembrou_token');
    localStorage.removeItem('lembrou_user');
    navigate('/');
  };

  const handleNovoLembrete = () => {
    if (stats?.plano_atual === 'FREE' && stats?.total_lembretes >= 20) {
      setShowUpgradeModal(true);
    } else {
      navigate('/lembretes/novo');
    }
  };

  const handleDelete = async (id, nome) => {
    setConfirmModal({ id, nome });
  };

  const confirmarExclusao = async () => {
    if (!confirmModal) return;
    const id = confirmModal.id;
    setConfirmModal(null);
    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.delete(`${API}/lembretes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lembrete excluído');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const abrirModalWhatsApp = (lembrete) => {
    if (!lembrete.cliente_telefone) {
      toast.error('Cliente não possui WhatsApp cadastrado');
      return;
    }

    if (!whatsappStatus?.habilitado) {
      toast.error('WhatsApp não está configurado no sistema');
      return;
    }

    setWhatsappModal(lembrete);
  };

  const enviarWhatsApp = async (lembrete, mensagemPersonalizada) => {
    setEnviandoWhatsApp(true);

    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.post(
        `${API}/lembretes/${lembrete.id}/enviar-whatsapp`,
        { mensagem_personalizada: mensagemPersonalizada },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ✅ SUCESSO - registrar envio de hoje e bloquear botão
      const agora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      setWhatsappEnviadoHoje(prev => ({ ...prev, [lembrete.id]: agora }));
      setWhatsappModal(null);
      setWhatsappResultModal({
        tipo: 'sucesso',
        nomeCliente: lembrete.cliente_nome,
        telefone: lembrete.cliente_telefone
      });
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Erro ao enviar WhatsApp';
      const status = error.response?.status || error.status;

      setWhatsappModal(null);

      // Normalizar texto removendo acentos para comparação segura
      const msgNorm = (msg || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      
      // Erro 429 = já enviado hoje
      // Checar: status HTTP, texto da mensagem, ou presença de horário na msg de bloqueio
      const isBloqueado = status === 429 
        || msgNorm.includes('ja enviada hoje')
        || msgNorm.includes('meia-noite')
        || msgNorm.includes('disponivel novamente')
        || (msgNorm.includes('enviada') && /\d{2}:\d{2}/.test(msg)); // "enviada às HH:MM"
      
      if (isBloqueado) {
        setWhatsappResultModal({
          tipo: 'bloqueado',
          mensagem: msg,
          nomeCliente: lembrete.cliente_nome
        });
        const hora = msg.match(/\d{2}:\d{2}/)?.[0] || 'hoje';
        setWhatsappEnviadoHoje(prev => ({ ...prev, [lembrete.id]: hora }));
      } else {
        setWhatsappResultModal({
          tipo: 'erro',
          mensagem: msg,
          nomeCliente: lembrete.cliente_nome
        });
      }
    } finally {
      setEnviandoWhatsApp(false);
    }
  };

  // ── Funções para Comparecimento (não-corretores) ──
  const marcarComparecimento = async (lembreteId, compareceu) => {
    setProcessandoComparecimento(lembreteId);
    try {
      const token = localStorage.getItem('lembrou_token');
      const res = await axios.patch(
        `${API}/lembretes/${lembreteId}/comparecimento`,
        { compareceu },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Atualiza localmente
      setLembretes(prev => prev.map(l => 
        l.id === lembreteId ? { ...l, status_comparecimento: res.data.status } : l
      ));
      
      if (compareceu) {
        toast.success('✅ Comparecimento registrado!');
      } else {
        toast.success('Registrado. Você pode enviar mensagem de remarcação.');
      }
      
      fetchData(); // Recarrega stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar');
    } finally {
      setProcessandoComparecimento(null);
    }
  };

  const enviarMsgRemarcacao = async (lembreteId) => {
    setEnviandoRemarcacao(lembreteId);
    try {
      const token = localStorage.getItem('lembrou_token');
      const res = await axios.post(
        `${API}/lembretes/${lembreteId}/enviar-remarcacao`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success('📱 Mensagem de remarcação enviada!');
        setLembretes(prev => prev.map(l => 
          l.id === lembreteId ? { ...l, status_comparecimento: 'aguardando_resposta' } : l
        ));
        fetchData();
      } else {
        toast.error(res.data.message || 'Erro ao enviar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setEnviandoRemarcacao(null);
    }
  };

  // Converte string de recorrência para número de dias
  const recorrenciaToDias = (recorrencia) => {
    const mapa = {
      'semanal': 7,
      'quinzenal': 15,
      'mensal': 30,
      'semestral': 180,
      'anual': 365,
    };
    // Se já vier como número, retorna direto
    if (!isNaN(parseInt(recorrencia))) return parseInt(recorrencia);
    return mapa[recorrencia?.toLowerCase()] || 30;
  };

  const renovarLembrete = async (lembreteId, recorrencia) => {
    const dias = recorrenciaToDias(recorrencia);
    setRenovandoLembrete(lembreteId);
    try {
      const token = localStorage.getItem('lembrou_token');
      const res = await axios.post(
        `${API}/lembretes/${lembreteId}/renovar`,
        { dias },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`🔄 ${res.data.message}`);
      fetchData(); // Recarrega tudo
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao renovar');
    } finally {
      setRenovandoLembrete(null);
    }
  };

  // Helper para filtrar lembretes pendentes (não-corretores)
  const lembretesPendentes = lembretes.filter(l => {
    const vencido = isVencido(l.data_renovacao);
    const statusPendente = !l.status_comparecimento || 
                           l.status_comparecimento === 'ativo' || 
                           l.status_comparecimento === 'pendente' ||
                           l.status_comparecimento === 'nao_compareceu' ||
                           l.status_comparecimento === 'aguardando_resposta';
    return vencido && statusPendente;
  });

  // ── Função para atualizar status do lembrete (só corretores) ──
  const atualizarStatus = async (lembreteId, novoStatus) => {
    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.patch(
        `${API}/lembretes/${lembreteId}/status`,
        { status: novoStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Atualiza localmente
      setLembretes(prev => prev.map(l => 
        l.id === lembreteId ? { ...l, status_lembrete: novoStatus } : l
      ));
      
      toast.success(`Status atualizado para ${STATUS_CORRETOR[novoStatus]?.label || novoStatus}`);
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // ── Ordenação ──
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Volta para primeira página ao ordenar
  };

  // ── Lógica de ordenação e paginação ──
  const sortedLembretes = [...lembretes].sort((a, b) => {
    if (sortField === 'data_renovacao') {
      const dateA = new Date(a.data_renovacao);
      const dateB = new Date(b.data_renovacao);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    if (sortField === 'status' && isCorretorSeguros) {
      const orderA = STATUS_CORRETOR[a.status_lembrete]?.order || 99;
      const orderB = STATUS_CORRETOR[b.status_lembrete]?.order || 99;
      return sortDirection === 'asc' ? orderA - orderB : orderB - orderA;
    }
    
    return 0;
  });

  // Gerar lista de meses disponíveis nos lembretes
  const mesesDisponiveis = (() => {
    const meses = {};
    sortedLembretes.forEach(l => {
      if (!l.data_renovacao) return;
      const dt = new Date(l.data_renovacao);
      const chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
        .replace('.', '').replace(' de ', ' ');
      if (!meses[chave]) meses[chave] = { chave, label, count: 0 };
      meses[chave].count++;
    });
    return Object.values(meses).sort((a, b) => a.chave.localeCompare(b.chave));
  })();

  // Filtrar por mês ativo
  const lembretesFilterados = mesFiltroAtivo
    ? sortedLembretes.filter(l => {
        if (!l.data_renovacao) return false;
        const dt = new Date(l.data_renovacao);
        const chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
        return chave === mesFiltroAtivo;
      })
    : sortedLembretes;

  // Agrupar lembretes por mês/ano
  const lembretesAgrupados = (() => {
    const grupos = {};
    lembretesFilterados.forEach(l => {
      if (!l.data_renovacao) {
        const chave = 'sem-data';
        if (!grupos[chave]) grupos[chave] = { titulo: 'Sem data', lembretes: [] };
        grupos[chave].lembretes.push(l);
        return;
      }
      const dt = new Date(l.data_renovacao);
      const chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const titulo = dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
        .replace(/^\w/, c => c.toUpperCase());
      if (!grupos[chave]) grupos[chave] = { chave, titulo, lembretes: [] };
      grupos[chave].lembretes.push(l);
    });
    return Object.values(grupos).sort((a, b) => (a.chave || '').localeCompare(b.chave || ''));
  })();

  // Paginação (sobre lembretes filtrados)
  const totalPages = Math.ceil(lembretesFilterados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLembretes = lembretesFilterados.slice(startIndex, startIndex + itemsPerPage);

  // Reset página quando muda items por página
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src="/icon-lembrou.jpg" alt="Lembrou?" className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">Lembrou?</h1>
                <p className="text-xs sm:text-sm text-gray-500 truncate max-w-[120px] sm:max-w-none">{user?.nome_empresa}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              {/* WhatsApp Status Indicator */}
              {whatsappStatus && (
                <div
                  className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-medium ${
                    whatsappStatus.habilitado && whatsappStatus.conexao?.connected
                      ? 'bg-green-100 text-green-700'
                      : whatsappStatus.habilitado
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                  title={
                    whatsappStatus.habilitado && whatsappStatus.conexao?.connected
                      ? 'WhatsApp conectado e pronto para enviar'
                      : whatsappStatus.habilitado
                        ? 'WhatsApp configurado mas desconectado'
                        : 'WhatsApp não configurado'
                  }
                >
                  <MessageCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="hidden md:inline">
                    {whatsappStatus.habilitado && whatsappStatus.conexao?.connected
                      ? 'WhatsApp OK'
                      : whatsappStatus.habilitado
                        ? 'Desconectado'
                        : 'WhatsApp Off'}
                  </span>
                  <span
                    className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                      whatsappStatus.habilitado && whatsappStatus.conexao?.connected
                        ? 'bg-green-500'
                        : whatsappStatus.habilitado
                          ? 'bg-yellow-500'
                          : 'bg-gray-400'
                    }`}
                  />
                </div>
              )}
              {/* Botão QR Code - escondido para corretores de seguros */}
              {!isCorretorSeguros && (
                <Button
                  data-testid="export-qrcode-button"
                  onClick={() => navigate('/meu-qrcode')}
                  variant="outline"
                  className="rounded-xl p-2 sm:px-3 border-2 border-primary text-primary hover:bg-primary hover:text-white"
                  title="Exportar QR Code"
                >
                  <QrCode className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Exportar QR Code</span>
                </Button>
              )}
              <Button
                data-testid="perfil-button"
                onClick={() => navigate('/perfil')}
                variant="ghost"
                className="rounded-xl p-2 sm:px-3"
                title="Perfil"
              >
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Perfil</span>
              </Button>
              <Button
                data-testid="ajuda-button"
                onClick={() => navigate('/faq')}
                variant="ghost"
                className="rounded-xl p-2 sm:px-3"
                title="Central de Ajuda"
              >
                <HelpCircle className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Ajuda</span>
              </Button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                className="rounded-xl p-2 sm:px-3"
                title="Sair"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Onboarding - aparece no primeiro acesso (exceto para corretores de seguros) */}
      {user && <Onboarding tipoUsuario={isCorretorSeguros ? 'corretor' : 'padrao'} />}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 🔥 LAYOUT FINAL - Proporcional e Profissional */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          
          {/* Card 1: Plano Atual (1 coluna) */}
          <Card className="p-6 rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Plano Atual</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.plano_atual}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500 mb-1">Uso do plano</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-semibold text-gray-900">{stats?.total_lembretes || 0}</span>
                  <span className="text-xs text-gray-400">/</span>
                  <span className="text-sm text-gray-600">
                    {stats?.limite_plano === 999999 ? '∞' : stats?.limite_plano}
                  </span>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  {stats?.total_lembretes || 0} {(stats?.total_lembretes || 0) === 1 ? 'lembrete ativo' : 'lembretes ativos'}
                </p>
              </div>
              {stats?.plano_atual === 'FREE' && (
                <button
                  onClick={() => navigate('/perfil')}
                  className="mt-2 w-full text-xs bg-gradient-to-r from-primary to-orange-500 text-white py-1.5 px-3 rounded-lg hover:shadow-md transition-all font-medium"
                >
                  Fazer Upgrade
                </button>
              )}
            </div>
          </Card>

          {/* Card 2: Próximos Vencimentos (2 colunas - ocupa mais espaço) */}
          <Card className="p-6 rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md transition-shadow md:col-span-2">
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-500 mb-1">Próximos Vencimentos</p>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {/* 7 dias */}
              <div className="bg-red-50 rounded-xl p-3 border-l-4 border-red-500">
                <p className="text-xs text-gray-500 mb-1">7 dias</p>
                <p className="text-2xl font-bold text-red-600">{stats?.vencendo_7_dias || 0}</p>
                <div className="flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3 text-red-400" />
                  <span className="text-xs text-red-400">Urgente</span>
                </div>
              </div>

              {/* 15 dias */}
              <div className="bg-orange-50 rounded-xl p-3 border-l-4 border-orange-400">
                <p className="text-xs text-gray-500 mb-1">15 dias</p>
                <p className="text-2xl font-bold text-orange-500">{stats?.vencendo_15_dias || 0}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3 text-orange-400" />
                  <span className="text-xs text-orange-400">Atenção</span>
                </div>
              </div>

              {/* 30 dias */}
              <div className="bg-yellow-50 rounded-xl p-3 border-l-4 border-yellow-400">
                <p className="text-xs text-gray-500 mb-1">30 dias</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.vencendo_30_dias || 0}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3 text-yellow-400" />
                  <span className="text-xs text-yellow-400">Normal</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Card 3: Ações/Pendentes (1 coluna - só para não-corretores) */}
          {!isCorretorSeguros && (
            <Card 
              className={`p-6 rounded-2xl border-2 shadow-sm cursor-pointer transition-all hover:shadow-md ${
                showPendentes 
                  ? 'border-purple-300 bg-purple-50' 
                  : 'border-gray-100'
              }`}
              onClick={() => setShowPendentes(!showPendentes)}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Ações</p>
                  <p className="text-4xl font-bold text-purple-600">
                    {(stats?.pendentes || 0) + (stats?.aguardando_resposta || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-purple-600 font-medium">
                  {showPendentes ? 'Clique para ocultar' : 'Clique para ver'}
                </p>
                {showPendentes ? (
                  <ChevronUp className="w-4 h-4 text-purple-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-purple-600" />
                )}
              </div>
            </Card>
          )}

        </div>

        {/* Seção de Ações Pendentes - aparece ao clicar no box */}
        {!isCorretorSeguros && showPendentes && lembretesPendentes.length > 0 && (
          <Card className="p-4 md:p-6 rounded-2xl border-2 border-purple-200 bg-purple-50 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-purple-600" />
                <h3 className="font-bold text-purple-900">Ações Pendentes</h3>
                <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">
                  {lembretesPendentes.length}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPendentes(false)}
                className="text-purple-600 hover:text-purple-800"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-3">
              {lembretesPendentes.slice(0, 5).map(lembrete => (
                <div 
                  key={lembrete.id}
                  className={`flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 rounded-xl ${
                    lembrete.status_comparecimento === 'aguardando_resposta' 
                      ? 'bg-orange-100 border border-orange-300' 
                      : lembrete.status_comparecimento === 'nao_compareceu'
                      ? 'bg-yellow-100 border border-yellow-300'
                      : 'bg-white border border-purple-200'
                  }`}
                >
                  {/* Info do cliente */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {lembrete.status_comparecimento === 'aguardando_resposta' && (
                        <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">
                          Aguardando resposta
                        </span>
                      )}
                      {lembrete.status_comparecimento === 'nao_compareceu' && (
                        <span className="text-xs bg-yellow-200 text-yellow-700 px-2 py-0.5 rounded-full">
                          Não compareceu
                        </span>
                      )}
                      {(!lembrete.status_comparecimento || lembrete.status_comparecimento === 'ativo' || lembrete.status_comparecimento === 'pendente') && (
                        <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded-full">
                          Vencido
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 mt-1">{lembrete.cliente_nome}</p>
                    <p className="text-sm text-gray-600">
                      {lembrete.tipo_servico} • Venceu em {formatarDataBR(lembrete.data_renovacao)}
                    </p>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2 flex-wrap">
                    {/* Se ainda não marcou comparecimento */}
                    {(!lembrete.status_comparecimento || lembrete.status_comparecimento === 'ativo' || lembrete.status_comparecimento === 'pendente') && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => marcarComparecimento(lembrete.id, true)}
                          disabled={processandoComparecimento === lembrete.id}
                          className="rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs"
                        >
                          {processandoComparecimento === lembrete.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <UserCheck className="w-3 h-3 mr-1" />
                          )}
                          Compareceu
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => marcarComparecimento(lembrete.id, false)}
                          disabled={processandoComparecimento === lembrete.id}
                          className="rounded-lg border-red-300 text-red-600 hover:bg-red-50 text-xs"
                        >
                          <UserX className="w-3 h-3 mr-1" />
                          Não foi
                        </Button>
                      </>
                    )}

                    {/* Se marcou que não compareceu - pode enviar msg */}
                    {lembrete.status_comparecimento === 'nao_compareceu' && (
                      <Button
                        size="sm"
                        onClick={() => enviarMsgRemarcacao(lembrete.id)}
                        disabled={enviandoRemarcacao === lembrete.id || !lembrete.cliente_telefone}
                        className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      >
                        {enviandoRemarcacao === lembrete.id ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <MessageCircle className="w-3 h-3 mr-1" />
                        )}
                        Enviar Remarcação
                      </Button>
                    )}

                    {/* Se está aguardando resposta ou compareceu - pode renovar */}
                    {(lembrete.status_comparecimento === 'aguardando_resposta' || lembrete.status_comparecimento === 'compareceu') && (
                      <Button
                        size="sm"
                        onClick={() => renovarLembrete(lembrete.id, lembrete.recorrencia || 30)}
                        disabled={renovandoLembrete === lembrete.id}
                        className="rounded-lg bg-primary hover:bg-primary-hover text-white text-xs"
                      >
                        {renovandoLembrete === lembrete.id ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        Renovar (+{recorrenciaToDias(lembrete.recorrencia || 30)} dias)
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              
              {lembretesPendentes.length > 5 && (
                <p className="text-sm text-purple-600 text-center">
                  E mais {lembretesPendentes.length - 5} pendentes...
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Limite Warning */}
        {stats?.plano_atual === 'FREE' && stats?.total_lembretes >= 15 && (
          <Card className="p-4 rounded-2xl border-2 border-red-200 bg-red-50 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">
                  Você está próximo do limite de 20 lembretes!
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Faça upgrade para PRO e tenha lembretes ilimitados.
                </p>
              </div>
              <Button
                data-testid="upgrade-warning-button"
                onClick={() => setShowUpgradeModal(true)}
                size="sm"
                className="rounded-xl bg-primary hover:bg-primary-hover"
              >
                Upgrade
              </Button>
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Lembretes dos Clientes</h2>
          <div className="flex gap-2 sm:gap-3">
            {/* Botão Importar - só para corretores de seguros */}
            {isCorretorSeguros && (
              <Button
                data-testid="importacoes-button"
                onClick={() => navigate('/importacoes')}
                variant="outline"
                className="rounded-xl sm:rounded-2xl border-2 text-sm px-3 py-2"
              >
                <Upload className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Importar CSV/PDF</span>
              </Button>
            )}
            <Button
              data-testid="novo-lembrete-button"
              onClick={handleNovoLembrete}
              className="rounded-xl sm:rounded-2xl bg-primary hover:bg-primary-hover shadow-md text-sm px-3 py-2"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Novo Lembrete</span>
            </Button>
          </div>
        </div>

        {/* Controles de Ordenação e Paginação */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          {/* Ordenação */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Ordenar por:</span>
            <button
              onClick={() => handleSort('data_renovacao')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                sortField === 'data_renovacao' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Data
              {sortField === 'data_renovacao' && (
                sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {/* Botão de ordenação por Status - só para corretores */}
            {isCorretorSeguros && (
              <button
                onClick={() => handleSort('status')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sortField === 'status' 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Circle className="w-4 h-4" />
                Status
                {sortField === 'status' && (
                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}
          </div>

          {/* Filtros de mês */}
          {mesesDisponiveis.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <button
                onClick={() => { setMesFiltroAtivo(null); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  !mesFiltroAtivo
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              {mesesDisponiveis.map(m => (
                <button
                  key={m.chave}
                  onClick={() => { setMesFiltroAtivo(m.chave); setCurrentPage(1); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
                    mesFiltroAtivo === m.chave
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {m.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    mesFiltroAtivo === m.chave
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {m.count}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Paginação - Itens por página */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Exibir:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
            >
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
            <span className="text-sm text-gray-500">
              ({lembretesFilterados.length} total)
            </span>
          </div>
        </div>

        {/* Lembretes - Desktop Table / Mobile Cards */}
        <Card className="rounded-2xl border shadow-sm overflow-hidden">
          {/* Desktop Table - hidden on mobile */}
          <div className="hidden md:block w-full overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contato
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Serviço
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('data_renovacao')}>
                    <div className="flex items-center gap-1">
                      Renovação
                      {sortField === 'data_renovacao' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  {/* Coluna Status - só para corretores */}
                  {isCorretorSeguros && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                      <div className="flex items-center justify-center gap-1">
                        Status
                        {sortField === 'status' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                  )}
                  {/* Coluna Comparecimento - só para não-corretores */}
                  {!isCorretorSeguros && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comparecimento
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLembretes.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        {isCorretorSeguros ? (
                          <>
                            <Bell className="w-12 h-12 text-gray-300" />
                            <p>Nenhum lembrete cadastrado ainda.</p>
                            <p className="text-sm">Clique em "Novo Lembrete" para começar a gerenciar suas apólices!</p>
                          </>
                        ) : (
                          <>
                            <QrCode className="w-12 h-12 text-gray-300" />
                            <p>Nenhum lembrete cadastrado ainda.</p>
                            <p className="text-sm">Compartilhe seu QR Code com clientes para eles começarem a adicionar lembretes!</p>
                            <Button
                              onClick={() => navigate('/meu-qrcode')}
                              className="rounded-2xl bg-primary hover:bg-primary-hover mt-2"
                            >
                              <QrCode className="w-4 h-4 mr-2" />
                              Ver Meu QR Code
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  lembretesAgrupados.map((grupo) => {
                    // Filtrar só os lembretes do grupo que estão na página atual
                    const lembretesDoGrupo = grupo.lembretes.filter(l => 
                      paginatedLembretes.some(p => p.id === l.id)
                    );
                    if (lembretesDoGrupo.length === 0) return null;
                    return (
                      <React.Fragment key={grupo.chave}>
                        {/* Header do grupo de mês */}
                        <tr className="bg-gray-50 border-y border-gray-200">
                          <td colSpan="7" className="px-4 py-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-primary" />
                                <span className="text-sm font-semibold text-gray-700">
                                  {grupo.titulo}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {lembretesDoGrupo.length} {lembretesDoGrupo.length === 1 ? 'lembrete' : 'lembretes'}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {/* Lembretes do grupo */}
                        {lembretesDoGrupo.map((lembrete) => (
                    <tr 
                      key={lembrete.id} 
                      className={`${
                        isVencido(lembrete.data_renovacao) 
                          ? 'bg-red-50 hover:bg-red-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]" title={lembrete.cliente_nome}>
                          {lembrete.cliente_nome}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600 truncate max-w-[180px]" title={lembrete.cliente_email}>
                          {lembrete.cliente_email || '-'}
                        </div>
                        {lembrete.cliente_telefone && (
                          <div className="text-xs text-gray-400">{lembrete.cliente_telefone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600 truncate max-w-[120px]" title={lembrete.tipo_servico}>
                          {lembrete.tipo_servico}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{formatarDataBR(lembrete.data_renovacao)}</div>
                      </td>
                      {/* Coluna Status - só para corretores */}
                      {isCorretorSeguros && (
                        <td className="px-4 py-3 text-center">
                          <select
                            value={lembrete.status_lembrete || 'nao_renovado'}
                            onChange={(e) => atualizarStatus(lembrete.id, e.target.value)}
                            className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-primary ${
                              lembrete.status_lembrete === 'renovado' ? 'bg-green-100 text-green-700' :
                              lembrete.status_lembrete === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
                              lembrete.status_lembrete === 'contatado' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            <option value="nao_renovado">🔴 Não Renovado</option>
                            <option value="contatado">🟡 Contatado</option>
                            <option value="em_andamento">🔵 Em Andamento</option>
                            <option value="renovado">🟢 Renovado</option>
                          </select>
                        </td>
                      )}
                      {/* Coluna Comparecimento - só para não-corretores */}
                      {!isCorretorSeguros && (
                        <td className="px-4 py-3 text-center">
                          {!isVencido(lembrete.data_renovacao) && (!lembrete.status_comparecimento || lembrete.status_comparecimento === 'ativo') ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <select
                              value={lembrete.status_comparecimento || 'pendente'}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'compareceu') marcarComparecimento(lembrete.id, true);
                                else if (val === 'nao_compareceu') marcarComparecimento(lembrete.id, false);
                              }}
                              disabled={processandoComparecimento === lembrete.id}
                              className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                                lembrete.status_comparecimento === 'compareceu'
                                  ? 'bg-green-100 text-green-700'
                                  : lembrete.status_comparecimento === 'aguardando_resposta'
                                  ? 'bg-orange-100 text-orange-700'
                                  : lembrete.status_comparecimento === 'nao_compareceu'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              <option value="pendente">🔴 Pendente</option>
                              <option value="compareceu">🟢 Compareceu</option>
                              <option value="nao_compareceu">🟡 Não foi</option>
                            </select>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {/* Botão WhatsApp */}
                          {lembrete.cliente_telefone && whatsappStatus?.habilitado && (
                            user?.plano !== 'PRO' ? (
                              <span
                                title="Envio manual disponível apenas no plano Profissional"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                                onClick={() => setShowUpgradeModal(true)}
                              >
                                <MessageCircle className="w-3 h-3" />
                                WhatsApp 🔒
                              </span>
                            ) : whatsappEnviadoHoje[lembrete.id] ? (
                              <span
                                title={`Enviado hoje às ${whatsappEnviadoHoje[lembrete.id]}. Disponível à meia-noite.`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                              >
                                <MessageCircle className="w-3 h-3" />
                                Enviado
                              </span>
                            ) : (
                              <button
                                data-testid={`whatsapp-button-${lembrete.id}`}
                                onClick={() => abrirModalWhatsApp(lembrete)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                                title="Enviar lembrete via WhatsApp"
                              >
                                <MessageCircle className="w-3 h-3" />
                                WhatsApp
                              </button>
                            )
                          )}
                          <button
                            data-testid={`edit-button-${lembrete.id}`}
                            onClick={() => navigate(`/lembretes/editar/${lembrete.id}`)}
                            className="text-primary hover:text-primary-hover px-2 py-1"
                          >
                            Editar
                          </button>
                          <button
                            data-testid={`delete-button-${lembrete.id}`}
                            onClick={() => handleDelete(lembrete.id, lembrete.cliente_nome)}
                            className="text-red-600 hover:text-red-900 px-2 py-1"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards - hidden on desktop */}
          <div className="md:hidden">
            {paginatedLembretes.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  {isCorretorSeguros ? (
                    <>
                      <Bell className="w-12 h-12 text-gray-300" />
                      <p>Nenhum lembrete cadastrado ainda.</p>
                      <p className="text-sm">Clique em + para começar!</p>
                    </>
                  ) : (
                    <>
                      <QrCode className="w-12 h-12 text-gray-300" />
                      <p>Nenhum lembrete cadastrado ainda.</p>
                      <p className="text-sm">Compartilhe seu QR Code com clientes para eles começarem a adicionar lembretes!</p>
                      <Button
                        onClick={() => navigate('/meu-qrcode')}
                        className="rounded-2xl bg-primary hover:bg-primary-hover mt-2"
                      >
                        <QrCode className="w-4 h-4 mr-2" />
                        Ver Meu QR Code
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {lembretesAgrupados.map((grupo) => {
                  const lembretesDoGrupo = grupo.lembretes.filter(l =>
                    paginatedLembretes.some(p => p.id === l.id)
                  );
                  if (lembretesDoGrupo.length === 0) return null;
                  return (
                    <React.Fragment key={grupo.chave}>
                      {/* Header do grupo mobile */}
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-primary" />
                          <span className="text-xs font-semibold text-gray-700">{grupo.titulo}</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {lembretesDoGrupo.length} {lembretesDoGrupo.length === 1 ? 'lembrete' : 'lembretes'}
                        </span>
                      </div>
                      {lembretesDoGrupo.map((lembrete) => (
                  <div 
                    key={lembrete.id} 
                    className={`p-4 ${
                      isVencido(lembrete.data_renovacao) 
                        ? 'bg-red-50 hover:bg-red-100' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Header do card */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {lembrete.cliente_nome}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          {lembrete.tipo_servico}
                        </p>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div>
                        <span className="text-gray-400">Renovação:</span>
                        <span className="ml-1 font-medium text-gray-700">{formatarDataBR(lembrete.data_renovacao)}</span>
                      </div>
                      {lembrete.cliente_telefone && (
                        <div className="truncate">
                          <span className="text-gray-400">Tel:</span>
                          <span className="ml-1 text-gray-700">{lembrete.cliente_telefone}</span>
                        </div>
                      )}
                    </div>

                    {/* Status para corretores */}
                    {isCorretorSeguros && (
                      <div className="mb-3">
                        <select
                          value={lembrete.status_lembrete || 'nao_renovado'}
                          onChange={(e) => atualizarStatus(lembrete.id, e.target.value)}
                          className={`w-full text-xs font-medium rounded-lg px-3 py-2 border-0 cursor-pointer focus:ring-2 focus:ring-primary ${
                            lembrete.status_lembrete === 'renovado' ? 'bg-green-100 text-green-700' :
                            lembrete.status_lembrete === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
                            lembrete.status_lembrete === 'contatado' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}
                        >
                          <option value="nao_renovado">🔴 Não Renovado</option>
                          <option value="contatado">🟡 Contatado</option>
                          <option value="em_andamento">🔵 Em Andamento</option>
                          <option value="renovado">🟢 Renovado</option>
                        </select>
                      </div>
                    )}

                    {/* Ações */}
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                      {lembrete.cliente_telefone && whatsappStatus?.habilitado && (
                        user?.plano !== 'PRO' ? (
                          <span
                            title="Envio manual disponível apenas no plano Profissional"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            <MessageCircle className="w-3 h-3" />
                            WhatsApp 🔒
                          </span>
                        ) : whatsappEnviadoHoje[lembrete.id] ? (
                          <span
                            title={`Enviado hoje às ${whatsappEnviadoHoje[lembrete.id]}. Disponível à meia-noite.`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                          >
                            <MessageCircle className="w-3 h-3" />
                            Enviado
                          </span>
                        ) : (
                          <button
                            onClick={() => abrirModalWhatsApp(lembrete)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            <MessageCircle className="w-3 h-3" />
                            WhatsApp
                          </button>
                        )
                      )}
                      <button
                        onClick={() => navigate(`/lembretes/editar/${lembrete.id}`)}
                        className="text-primary hover:text-primary-hover text-xs font-medium px-3 py-1.5"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(lembrete.id, lembrete.cliente_nome)}
                        className="text-red-600 hover:text-red-900 text-xs font-medium px-3 py-1.5"
                      >
                        Excluir
                      </button>
                    </div>
                    </div>
                  ))}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* Paginação - Navegação entre páginas */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-4 py-3 bg-white rounded-xl border shadow-sm">
            <div className="text-sm text-gray-500">
              Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, lembretesFilterados.length)} de {lembretesFilterados.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-primary text-white'
                          : 'bg-white border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}

      {/* Modal Confirmação Exclusão */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              Excluir lembrete?
            </h2>
            <p className="text-gray-500 text-center text-sm mb-6">
              Tem certeza que deseja excluir o lembrete de{' '}
              <strong className="text-gray-700">{confirmModal.nome}</strong>?
              <br />Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarExclusao}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}

      {whatsappModal && (
        <WhatsAppModal
          lembrete={whatsappModal}
          user={user}
          onClose={() => setWhatsappModal(null)}
          onSend={enviarWhatsApp}
          enviando={enviandoWhatsApp}
        />
      )}

      {/* Modal de Resultado WhatsApp */}
      {whatsappResultModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in duration-300">
            {/* Botão Fechar */}
            <button
              onClick={() => setWhatsappResultModal(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {whatsappResultModal.tipo === 'bloqueado' ? (
              // 🔒 MODAL BLOQUEADO
              <>
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-5xl">🔒</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
                  Já enviado hoje!
                </h2>
                <p className="text-gray-600 text-center mb-6">
                  {whatsappResultModal.mensagem}
                </p>
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-6">
                  <p className="text-sm text-amber-800 text-center">
                    ⏰ O botão será liberado automaticamente à <strong>meia-noite</strong> de hoje.
                  </p>
                </div>
                <Button
                  onClick={() => setWhatsappResultModal(null)}
                  className="w-full rounded-2xl h-12 bg-gray-600 hover:bg-gray-700 text-lg font-medium"
                >
                  Entendi
                </Button>
              </>
            ) : whatsappResultModal.tipo === 'sucesso' ? (
              // ✅ MODAL DE SUCESSO
              <>
                {/* Ícone de Sucesso Animado */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-in zoom-in duration-500">
                      <CheckCircle2 className="w-16 h-16 text-green-600" />
                    </div>
                    <div className="absolute -top-2 -right-2 animate-bounce">
                      <MessageCircle className="w-8 h-8 text-green-500" />
                    </div>
                  </div>
                </div>

                {/* Título */}
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
                  Enviado! 🎉
                </h2>

                {/* Mensagem */}
                <p className="text-lg text-gray-700 text-center mb-6">
                  WhatsApp enviado com sucesso para <strong>{whatsappResultModal.nomeCliente}</strong>!
                </p>

                {/* Informações */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <Phone className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-900 mb-1">
                        Mensagem entregue
                      </p>
                      <p className="text-sm text-green-700">
                        Para: {whatsappResultModal.telefone}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Botão Fechar */}
                <Button
                  onClick={() => setWhatsappResultModal(null)}
                  className="w-full rounded-2xl h-12 bg-green-600 hover:bg-green-700 text-lg font-medium"
                >
                  Entendi!
                </Button>
              </>
            ) : (
              // ❌ MODAL DE ERRO
              <>
                {/* Ícone de Erro */}
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center animate-in zoom-in duration-500">
                    <AlertCircle className="w-16 h-16 text-red-600" />
                  </div>
                </div>

                {/* Título */}
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
                  Ops! ⚠️
                </h2>

                {/* Mensagem */}
                <p className="text-lg text-gray-700 text-center mb-4">
                  Não foi possível enviar o WhatsApp para <strong>{whatsappResultModal.nomeCliente}</strong>.
                </p>

                {/* Erro Detalhado */}
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6">
                  <p className="text-sm text-red-800">
                    <strong>Erro:</strong> {whatsappResultModal.mensagem}
                  </p>
                </div>

                {/* Ações */}
                <div className="space-y-3">
                  <Button
                    onClick={() => {
                      setWhatsappResultModal(null);
                      setWhatsappModal(whatsappResultModal.lembrete);
                    }}
                    className="w-full rounded-2xl h-12 bg-primary hover:bg-primary-hover text-lg font-medium"
                  >
                    Tentar Novamente
                  </Button>
                  <button
                    onClick={() => setWhatsappResultModal(null)}
                    className="w-full rounded-2xl h-12 text-gray-600 hover:text-gray-800 text-lg font-medium"
                  >
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
